// Gift cards — vocabulario y cuentas de la UI (docs/spec-gift-cards.md en el backend).
//
// Lo que no es obvio y por eso vive acá, con test:
//
//   - La gift card que se VENDE no pasa por `purchase/calculatePrice` (el backend la agrega
//     aparte, fuera de los descuentos: FR-006). El total a cobrar es el precio calculado MÁS
//     monto × cantidad, y esa suma se hace en un solo lugar: `totalACobrar`.
//   - El saldo a favor nunca paga la gift card vendida (FR-007): el tope del saldo sale de
//     `baseDelSaldo`, que no la incluye. Si además se USA una gift card, el saldo cubre como
//     máximo la diferencia (FR-040).
//   - `vencida` no es un estado guardado: es una `activa` con la fecha pasada (FR-084). El
//     backend ya la manda derivada; `etiquetaEstado` la deriva igual por si llega una activa
//     vieja (p. ej. una pantalla abierta desde ayer).

export const MONTO_MIN = 1;
export const MONTO_MAX = 100000;
// El backend (giftCardService.parseSale) rechaza más de 50 tarjetas en una venta.
export const CANTIDAD_MAX = 50;

// Sólo se sacan espacios, guiones y minúsculas: validar el alfabeto y el dígito de control es
// del backend (FR-020, FR-021), que es quien conoce la tabla.
const NO_ALFANUMERICO = /[^A-Z0-9]/g;

export function normalizarCodigo(code) {
    return String(code == null ? '' : code).toUpperCase().replace(NO_ALFANUMERICO, '');
}

// 'abcd efgh-jk' → 'ABCD-EFGH-JK': grupos de 4-4-2, que es como se copia en la tarjeta.
export function formatearCodigo(code) {
    const n = normalizarCodigo(code);
    if (n === '') return '';
    return [n.slice(0, 4), n.slice(4, 8), n.slice(8)].filter((g) => g !== '').join('-');
}

const ESTADOS = {
    activa: { label: 'Activa', badge: 'badge-success' },
    usada: { label: 'Usada', badge: 'badge-secondary' },
    canjeada: { label: 'Canjeada a saldo', badge: 'badge-info' },
    anulada: { label: 'Anulada', badge: 'badge-danger' },
    vencida: { label: 'Vencida', badge: 'badge-warning' },
};

// Filtros del listado, en el orden en que se muestran. `trabadas` no es un estado: son las
// usadas en una compra que quedó sin pagar (la tarjeta está tomada por una venta que no cerró).
export const FILTROS_ESTADO = [
    { value: '', label: 'Todas' },
    { value: 'activa', label: 'Activas' },
    { value: 'usada', label: 'Usadas' },
    { value: 'canjeada', label: 'Canjeadas' },
    { value: 'anulada', label: 'Anuladas' },
    { value: 'vencida', label: 'Vencidas' },
    { value: 'trabadas', label: 'Trabadas (compra sin pagar)' },
];

// 'YYYY-MM-DD' de HOY en la zona del navegador. No `toISOString()`: de noche ya es mañana en UTC.
export function hoyLocal(ahora = new Date()) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${ahora.getFullYear()}-${pad(ahora.getMonth() + 1)}-${pad(ahora.getDate())}`;
}

// Estado visible de una tarjeta: `vencida` si está activa y su último día válido ya pasó.
export function estadoVisible(card, hoy = hoyLocal()) {
    if (!card) return '';
    const vence = card.validUntil ? String(card.validUntil).slice(0, 10) : null;
    if (card.status === 'activa' && vence && vence < hoy) return 'vencida';
    return card.status;
}

export function etiquetaEstado(card, hoy = hoyLocal()) {
    const key = estadoVisible(card, hoy);
    const estado = ESTADOS[key] || { label: key || '-', badge: 'badge-light' };
    return { key, ...estado };
}

// Sólo una activa (no vencida) se puede canjear o anular (FR-050, FR-071).
export function esUtilizable(card, hoy = hoyLocal()) {
    return estadoVisible(card, hoy) === 'activa';
}

// Mensaje de validación del monto a vender, o null si está bien (FR-002).
export function errorDeMonto(raw) {
    if (raw === '' || raw == null) return null;
    const n = Number(raw);
    if (!Number.isInteger(n) || n < MONTO_MIN || n > MONTO_MAX) {
        return `El monto tiene que ser en pesos enteros, entre $${MONTO_MIN} y $${MONTO_MAX.toLocaleString('de-DE')}.`;
    }
    return null;
}

export function errorDeCantidad(raw) {
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1 || n > CANTIDAD_MAX) {
        return `La cantidad tiene que ser un entero entre 1 y ${CANTIDAD_MAX}.`;
    }
    return null;
}

// { amount, quantity } listo para el body de `purchase`, o null si no hay venta válida.
export function ventaValida(rawAmount, rawQuantity) {
    if (rawAmount === '' || rawAmount == null) return null;
    if (errorDeMonto(rawAmount) || errorDeCantidad(rawQuantity)) return null;
    return { amount: Number(rawAmount), quantity: Number(rawQuantity) };
}

// Lo que suman las gift cards que se venden (monto × cantidad), 0 si no hay.
export function totalDeLaVenta(sale) {
    if (!sale) return 0;
    return (Number(sale.amount) || 0) * (Number(sale.quantity) || 0);
}

// Total a cobrar: el precio calculado por el backend (sin la línea de gift card) + las tarjetas.
export function totalACobrar(finalPrice, sale) {
    return (Number(finalPrice) || 0) + totalDeLaVenta(sale);
}

// Lo máximo que puede cubrir el saldo a favor: los productos (o lo que queda de ellos después de
// la gift card usada). NUNCA las gift cards vendidas (FR-007, FR-040).
export function baseDelSaldo(finalPrice, giftCardUse) {
    if (giftCardUse && giftCardUse.difference != null) {
        return Math.max(0, Number(giftCardUse.difference) || 0);
    }
    return Math.max(0, Number(finalPrice) || 0);
}

// Tope del saldo aplicable: la base menos el dinero a favor de un cambio, y el saldo del cliente
// si ya se consultó.
export function topeDelSaldo({ finalPrice, giftCardUse = null, inFavour = 0, balance = null }) {
    const owed = Math.max(0, baseDelSaldo(finalPrice, giftCardUse) - (Number(inFavour) || 0));
    const disponible = balance != null ? Number(balance) : Infinity;
    return Math.max(0, Math.min(owed, disponible));
}

// Fecha y hora de una columna DATETIME del backend. Se leen los dígitos tal cual: la DB guarda
// hora de pared de Montevideo y Sequelize la etiqueta como UTC; pasarla por `new Date()` la
// correría 3 horas.
const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;
export function fechaHora(value) {
    if (!value) return '—';
    const m = WALL_CLOCK_RE.exec(String(value));
    if (!m) return String(value);
    const fecha = `${m[3]}/${m[2]}/${m[1]}`;
    return m[4] ? `${fecha} ${m[4]}:${m[5]}` : fecha;
}

export function textoVencimiento(validUntil) {
    if (!validUntil) return 'Sin vencimiento';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(validUntil));
    return m ? `Vence el ${m[3]}/${m[2]}/${m[1]}` : String(validUntil);
}

// Mensaje de error de una respuesta del backend (`{ error, message, data }`).
export function mensajeDeError(body) {
    if (!body) return 'No se pudo completar la operación';
    return (body.data && typeof body.data.message === 'string' && body.data.message)
        || body.message
        || 'No se pudo completar la operación';
}

// --- Premios y cortesías (backend docs/spec-premios-cortesias.md) -------------------------

export const ORIGENES = {
    venta: 'Vendida',
    premio: 'Premio',
    cortesia: 'Cortesía',
};

export const FILTROS_ORIGEN = [
    { value: '', label: 'Todas' },
    { value: 'venta', label: 'Vendidas' },
    { value: 'premio', label: 'Premios de eventos' },
    { value: 'cortesia', label: 'Cortesías' },
];

// El backend acepta de 2 a 10 partes (FR-005).
export const REPARTO_MIN = 2;
export const REPARTO_MAX = 10;

// Partes iguales en pesos enteros; lo que no divide exacto va a la primera, así la suma cierra.
// 1.001 entre 4 → [251, 250, 250, 250].
export function repartoIgual(total, partes) {
    const n = Math.max(1, Math.floor(Number(partes) || 1));
    const monto = Math.floor(Number(total) || 0);
    const base = Math.floor(monto / n);
    return Array.from({ length: n }, (_, i) => (i === 0 ? monto - base * (n - 1) : base));
}

// Qué le falta al reparto para poder confirmarlo, o null si está bien. Las cédulas las valida el
// backend (dígito verificador); acá sólo lo que el cajero ve mientras tipea.
export function errorDeReparto(partes, total) {
    if (!Array.isArray(partes) || partes.length < REPARTO_MIN) return `Repartí entre al menos ${REPARTO_MIN} cédulas.`;
    if (partes.length > REPARTO_MAX) return `Como máximo ${REPARTO_MAX} cédulas.`;
    const cedulas = partes.map((p) => String(p.cedula || '').replace(/\D/g, ''));
    if (cedulas.some((c) => c === '')) return 'Falta alguna cédula.';
    if (new Set(cedulas).size !== cedulas.length) return 'Hay una cédula repetida.';
    const montos = partes.map((p) => Number(p.amount));
    if (montos.some((m) => !Number.isInteger(m) || m < 1)) return 'Cada parte tiene que ser un monto entero de al menos $1.';
    const suma = montos.reduce((a, b) => a + b, 0);
    if (suma !== Number(total)) return `Las partes suman $ ${suma} y la tarjeta vale $ ${Number(total)}.`;
    return null;
}
